import React, { useMemo } from 'react';
import { BarChart, XAxis, YAxis, Bar, LabelList, Cell } from 'recharts';
import { useTheme } from '@mui/material';
import AutoSizer from 'react-virtualized-auto-sizer';
import { Fund } from '../api/useFunds';
import { ORDERED_BICS, bicToName } from '../utils/bicMappings';
import { toEuroWholeNumber } from '../utils/numberFormat';

interface ChartRecord {
  name: string;
  bic: Bic;
  portfolioCommission: number;
}

interface Props {
  bic: Bic;
  fundsMap: Map<Fund, number>;
  onClick: (bic: Bic) => void;
}

const chartData = ORDERED_BICS.reduce((agg, bic) => {
  return agg.set(bic, {
    name: bicToName(bic, true),
    bic: bic,
    portfolioCommission: 0,
  });
}, new Map<Bic, ChartRecord>() as Map<Bic, ChartRecord>);

const clearChartData = (chartDataWithValues: Map<Bic, ChartRecord>) => {
  ORDERED_BICS.forEach((bic) => {
    const record = chartDataWithValues.get(bic);
    if (record) {
      record.portfolioCommission = 0;
    }
  });
};

export const CashbackComparisonChart = ({ bic, fundsMap, onClick }: Props) => {
  const theme = useTheme();

  const data = useMemo(() => {
    const chartDataWithValues = new Map(chartData);
    clearChartData(chartDataWithValues);

    fundsMap.forEach((investedAmount, fund) => {
      fund.courtages?.forEach((courtage) => {
        const chartDataRecord = chartDataWithValues.get(courtage.bic as Bic);
        if (chartDataRecord && investedAmount) {
          chartDataRecord.portfolioCommission +=
            (courtage.portfolioCommission || 0) * investedAmount;
        }
      });
    });
    return Array.from(chartDataWithValues.values());
  }, [fundsMap]);

  const handleClick = (data: any) => {
    onClick(data.bic);
  };

  return (
    <AutoSizer>
      {({ width, height }: { width: number; height: number }) => (
        <BarChart
          width={width}
          height={height}
          data={data}
          margin={{
            top: 20,
            right: 0,
            left: 5,
            bottom: 5,
          }}
        >
          <XAxis
            dataKey="name"
            stroke={theme.palette.text.primary}
            angle={-45}
            textAnchor="end"
            height={120}
            interval={0}
          />
          <YAxis tick={false} width={1} stroke={theme.palette.text.disabled} />
          ink
          <Bar
            dataKey="portfolioCommission"
            stackId="a"
            fill={theme.palette.secondary.main}
            onClick={handleClick}
          >
            {data.map((entry, index) => (
              <Cell
                cursor="pointer"
                key={`cell-${index}`}
                fill={entry.bic === bic ? theme.palette.primary.main : theme.palette.secondary.dark}
              />
            ))}
            <LabelList
              dataKey="portfolioCommission"
              position="top"
              offset={10}
              formatter={(label: any) => {
                return `${toEuroWholeNumber(label)}`;
              }}
            />
          </Bar>
        </BarChart>
      )}
    </AutoSizer>
  );
};
